import { fillRepresentativeTemplate } from "@repo/shared";
import type { AssessmentAttribution, MemberRepresentative } from "@repo/shared/types";
import { InfoNote } from "./info-note";

/**
 * QP-184 — MiFID II Art. 54(6) attribution note. When a representative acts on
 * the client's behalf (Company/Group always; Person with a registered
 * POA/guardian), knowledge & experience answers are the representative's, not
 * the client's. QL-654: a client can have several representatives; `name` then
 * lists them all (`joinRepresentativeNames` in `@repo/shared`) and `count` says how many.
 *
 * QL-505 superseded this with the server-decided `AssessmentAttribution`
 * (`toAttribution`); `ActingRepresentative` stays only as the fallback used
 * when the caller has not been wired to pass `attribution` yet.
 */
export type ActingRepresentative = {
  /** The representative's name; null when acting via an unregistered representative. */
  name: string | null;
  /** How many representatives `name` lists (plural wording when 2+). */
  count?: number;
  /**
   * QL-660 — set when `name` is the POA/guardian of a group's flagged
   * representative member: the note then says whose POA it is.
   */
  memberRepresentative?: MemberRepresentative | null;
};

/** Fill the note with the representative's name or the generic fallback. */
export function representativeNoteText(
  template: (name: string) => string,
  rep: ActingRepresentative,
  genericName: string,
  /** QL-660 — with `viaMemberTemplate`, a POA acting for a group's representative member is named in full. */
  language?: string,
  viaMemberTemplate?: string,
): string {
  if (rep.memberRepresentative && viaMemberTemplate) {
    return fillRepresentativeTemplate(
      viaMemberTemplate,
      rep.memberRepresentative,
      language ?? "en_GB",
    );
  }
  const text = template(rep.name || genericName);
  // Several representatives: the English templates say "who acts".
  return (rep.count ?? 0) > 1 ? text.replace("who acts", "who act") : text;
}

/**
 * QL-505 — whether an attribution banner should show, and the name to put in
 * it. `null` = no banner at all (`none` and `members` attribution; a group
 * without a representative renders member lines instead of a banner).
 * `{ name }` = show the banner; `name` null means show it with the generic
 * fallback (an unregistered representative). Prefers the server-decided
 * `attribution` and falls back to the legacy `actingRepresentative` prop only
 * while a caller has not been wired to pass `attribution` (it stays
 * `undefined`, never `null`, until then).
 */
export function resolveAttributionBanner(
  attribution: AssessmentAttribution | undefined,
  actingRepresentative: ActingRepresentative | null | undefined,
): { name: string | null; memberRepresentative?: MemberRepresentative | null } | null {
  if (attribution) {
    return attribution.kind === "representative"
      ? { name: attribution.name, memberRepresentative: attribution.memberRepresentative }
      : null;
  }
  return actingRepresentative ? { name: actingRepresentative.name } : null;
}

export function RepresentativeNote({ text }: { text: string }) {
  return <InfoNote text={text} />;
}
